import { View, Text, styled, useTheme } from 'tamagui'
import { Paperclip, Smile, X, Bold, Italic, Code, Type } from '@tamagui/lucide-icons'
import { useState, useRef, useEffect, useCallback } from 'react'
import { useAppStore, useServerStore } from '@/store'
import { useToastStore } from '@/store/toastStore'
import { messages, users } from '@/mocks'
import { MentionAutocomplete, getAutocompleteMatches } from './MentionAutocomplete'
import { TypingIndicator } from './TypingIndicator'

const InputWrapper = styled(View, {
  flexDirection: 'column',
  gap: 0,
})

const ReplyBar = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  backgroundColor: '$sidebar',
  borderLeftWidth: 3,
  borderLeftColor: '$accent',
  borderTopLeftRadius: '$borderRadiusMd',
  borderTopRightRadius: '$borderRadiusMd',
  paddingHorizontal: 12,
  paddingVertical: 8,
  gap: 8,
})

const ReplyText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 13,
  color: '$sidebarMuted',
  flex: 1,
  // @ts-expect-error -- web overflow
  style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
})

const ReplyAuthor = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 13,
  fontWeight: '600',
  color: '$accent',
})

const AttachmentChipsRow = styled(View, {
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: 6,
  paddingHorizontal: 12,
  paddingVertical: 6,
  backgroundColor: '$sidebar',
  borderTopLeftRadius: '$borderRadiusMd',
  borderTopRightRadius: '$borderRadiusMd',
})

const AttachmentChip = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 6,
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusMd',
  paddingHorizontal: 8,
  paddingVertical: 4,
  borderWidth: 1,
  borderColor: '$borderDefault',
})

const AttachmentName = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 12,
  color: '$surfaceForeground',
  maxWidth: 160,
  // @ts-expect-error -- web overflow
  style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
})

const AttachmentSize = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 11,
  color: '$sidebarMuted',
})

const InputContainer = styled(View, {
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusLg',
  borderWidth: 1,
  borderColor: '$borderDefault',
  flexDirection: 'row',
  alignItems: 'flex-end',
  paddingHorizontal: 12,
  paddingVertical: 8,
  gap: 8,
  // @ts-expect-error -- web transition
  style: { transition: 'border-color 150ms ease, box-shadow 250ms ease' },
})

const InputButton = styled(View, {
  width: 28,
  height: 28,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

const ToolbarRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 2,
  paddingHorizontal: 12,
  paddingVertical: 4,
  backgroundColor: '$sidebar',
  borderBottomLeftRadius: '$borderRadiusMd',
  borderBottomRightRadius: '$borderRadiusMd',
})

const ToolbarButton = styled(View, {
  width: 28,
  height: 28,
  borderRadius: '$borderRadiusSm',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

const CloseButton = styled(View, {
  width: 20,
  height: 20,
  borderRadius: '$borderRadiusFull',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

interface PendingAttachment {
  id: string
  file: File
  name: string
  size: number
  progress: number // -1 = not uploading, 0-100 = uploading
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function wrapSelection(
  textarea: HTMLTextAreaElement,
  prefix: string,
  suffix: string,
  setValue: (v: string) => void,
) {
  const start = textarea.selectionStart
  const end = textarea.selectionEnd
  const text = textarea.value
  const selected = text.substring(start, end)
  const newText = text.substring(0, start) + prefix + selected + suffix + text.substring(end)
  setValue(newText)
  // Restore cursor after the wrapped text
  requestAnimationFrame(() => {
    textarea.focus()
    textarea.setSelectionRange(start + prefix.length, end + prefix.length)
  })
}

interface MessageInputProps {
  channelId: string
}

interface AutocompleteState {
  isOpen: boolean
  type: '@' | '#'
  query: string
  selectedIndex: number
}

export function MessageInput({ channelId }: MessageInputProps) {
  const [focused, setFocused] = useState(false)
  const [value, setValue] = useState('')
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([])
  const [showToolbar, setShowToolbar] = useState(false)
  const [autocomplete, setAutocomplete] = useState<AutocompleteState>({ isOpen: false, type: '@', query: '', selectedIndex: 0 })
  const [slowmodeRemaining, setSlowmodeRemaining] = useState(0)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { activeServerId, setEmojiPickerOpen, replyingToMessageId, setReplyingToMessageId } = useAppStore()
  const addToast = useToastStore((s) => s.addToast)
  const server = useServerStore((s) => activeServerId ? s.servers[activeServerId] : undefined)
  const theme = useTheme()
  const channel = server?.channels.find(c => c.id === channelId)
  const isUploading = pendingAttachments.some(a => a.progress >= 0 && a.progress < 100)
  const placeholder = channel ? `Message #${channel.name}` : 'Type a message...'

  // Slowmode countdown
  useEffect(() => {
    if (slowmodeRemaining <= 0) return
    const timer = setInterval(() => {
      setSlowmodeRemaining((prev) => {
        if (prev <= 1) { clearInterval(timer); return 0 }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [slowmodeRemaining])

  // Resolve theme colors for the native textarea
  const textColor = theme.surfaceForeground?.val || '#E2E8F0'

  // Find reply message and author
  let replyMessage: { content: string; authorName: string } | null = null
  if (replyingToMessageId) {
    for (const channelMsgs of Object.values(messages)) {
      const msg = channelMsgs.find(m => m.id === replyingToMessageId)
      if (msg) {
        const author = users[msg.authorId]
        replyMessage = {
          content: msg.content,
          authorName: author?.displayName || 'Unknown',
        }
        break
      }
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return
    const newAttachments: PendingAttachment[] = Array.from(files).map((file) => ({
      id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      name: file.name,
      size: file.size,
      progress: -1,
    }))
    setPendingAttachments((prev) => [...prev, ...newAttachments])
    // Reset file input so selecting the same file again triggers onChange
    e.target.value = ''
  }

  const removeAttachment = (id: string) => {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== id))
  }

  const hasReplyBar = replyMessage !== null
  const hasAttachments = pendingAttachments.length > 0

  return (
    <InputWrapper>
      {/* Typing indicator */}
      <View paddingLeft={12} marginBottom={-4}>
        <TypingIndicator channelId={channelId} />
      </View>

      {/* Reply context bar */}
      {hasReplyBar && replyMessage && (
        <ReplyBar>
          <ReplyText>
            Replying to <ReplyAuthor>@{replyMessage.authorName}</ReplyAuthor>
            {'  '}
            {replyMessage.content.length > 80
              ? replyMessage.content.slice(0, 80) + '...'
              : replyMessage.content}
          </ReplyText>
          <CloseButton onPress={() => setReplyingToMessageId(null)}>
            <X size={12} color="$sidebarMuted" />
          </CloseButton>
        </ReplyBar>
      )}

      {/* Attachment chips */}
      {hasAttachments && (
        <AttachmentChipsRow
          {...(hasReplyBar && {
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          })}
        >
          {pendingAttachments.map((att) => {
            const uploading = att.progress >= 0 && att.progress < 100
            return (
              <AttachmentChip key={att.id} position="relative" overflow="hidden">
                <Paperclip size={12} color="$sidebarMuted" />
                <AttachmentName>{att.name}</AttachmentName>
                <AttachmentSize>
                  {uploading ? `${att.progress}%` : formatFileSize(att.size)}
                </AttachmentSize>
                <CloseButton
                  onPress={() => !uploading && removeAttachment(att.id)}
                  opacity={uploading ? 0.3 : 1}
                  // @ts-expect-error -- web cursor
                  style={{ cursor: uploading ? 'default' : 'pointer' }}
                >
                  <X size={10} color="$sidebarMuted" />
                </CloseButton>
                {uploading && (
                  <View
                    position="absolute"
                    bottom={0}
                    left={0}
                    height={2}
                    backgroundColor="$accent"
                    borderRadius="$borderRadiusSm"
                    // @ts-expect-error -- web width percentage
                    style={{ width: `${att.progress}%`, transition: 'width 150ms ease' }}
                  />
                )}
              </AttachmentChip>
            )
          })}
        </AttachmentChipsRow>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      <InputContainer
        borderColor={focused ? '$accent' : '$borderDefault'}
        {...((hasReplyBar || hasAttachments) && {
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        })}
        {...(showToolbar && {
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        })}
        // @ts-expect-error -- web box-shadow
        style={{
          boxShadow: focused
            ? (theme.shadowInputFocus?.val || 'none')
            : 'none',
          transition: 'border-color 150ms ease, box-shadow 250ms ease',
        }}
      >
        <InputButton onPress={() => fileInputRef.current?.click()}>
          <Paperclip size={16} color="$sidebarMuted" />
        </InputButton>

        <View flex={1} position="relative">
          {/* Mention autocomplete */}
          {autocomplete.isOpen && (
            <View position="absolute" bottom="100%" left={0} right={0} marginBottom={4} zIndex={1000}>
              <MentionAutocomplete
                type={autocomplete.type}
                query={autocomplete.query}
                selectedIndex={autocomplete.selectedIndex}
                onSelect={(username, display) => {
                  const textarea = inputRef.current
                  if (!textarea) return
                  const cursorPos = textarea.selectionStart
                  const textBefore = value.substring(0, cursorPos)
                  const textAfter = value.substring(cursorPos)
                  const triggerMatch = textBefore.match(/([@#])(\S*)$/)
                  if (triggerMatch) {
                    const start = cursorPos - triggerMatch[0].length
                    const replacement = autocomplete.type === '@' ? `@${username} ` : `#${username} `
                    setValue(value.substring(0, start) + replacement + textAfter)
                  }
                  setAutocomplete({ isOpen: false, type: '@', query: '', selectedIndex: 0 })
                }}
              />
            </View>
          )}
          <textarea
            ref={inputRef}
            value={value}
            onChange={(e) => {
              const newValue = e.target.value
              setValue(newValue)
              // Check for mention/channel autocomplete trigger
              const textarea = e.target
              const cursorPos = textarea.selectionStart
              const textBefore = newValue.substring(0, cursorPos)
              const triggerMatch = textBefore.match(/([@#])(\S*)$/)
              if (triggerMatch) {
                const type = triggerMatch[1] as '@' | '#'
                const query = triggerMatch[2]
                const matchCount = getAutocompleteMatches(type, query, server ?? undefined)
                if (matchCount > 0) {
                  setAutocomplete({ isOpen: true, type, query, selectedIndex: 0 })
                } else {
                  setAutocomplete((prev) => ({ ...prev, isOpen: false }))
                }
              } else {
                setAutocomplete((prev) => ({ ...prev, isOpen: false }))
              }
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={placeholder}
            rows={1}
            style={{
              background: 'transparent',
              border: 'none',
              outline: 'none',
              resize: 'none',
              color: textColor,
              fontFamily: '$fontMono',
              fontSize: 14,
              lineHeight: '22px',
              width: '100%',
              maxHeight: 200,
              opacity: slowmodeRemaining > 0 ? 0.5 : 1,
            }}
            onKeyDown={(e) => {
              if (autocomplete.isOpen) {
                const matchCount = getAutocompleteMatches(autocomplete.type, autocomplete.query, server ?? undefined)
                if (e.key === 'ArrowUp') {
                  e.preventDefault()
                  setAutocomplete((prev) => ({ ...prev, selectedIndex: Math.max(0, prev.selectedIndex - 1) }))
                  return
                }
                if (e.key === 'ArrowDown') {
                  e.preventDefault()
                  setAutocomplete((prev) => ({ ...prev, selectedIndex: Math.min(matchCount - 1, prev.selectedIndex + 1) }))
                  return
                }
                if (e.key === 'Enter') {
                  e.preventDefault()
                  // Let MentionAutocomplete handle via onSelect — simulate click on selected item
                  // For simplicity, we build the replacement here
                  const allUsers = Object.values(users)
                  const q = autocomplete.query.toLowerCase()
                  if (autocomplete.type === '@') {
                    const matches = allUsers.filter((u) => u.displayName.toLowerCase().includes(q) || u.username.toLowerCase().includes(q)).slice(0, 5)
                    const selected = matches[autocomplete.selectedIndex]
                    if (selected) {
                      const cursorPos = inputRef.current?.selectionStart || value.length
                      const textBefore = value.substring(0, cursorPos)
                      const textAfter = value.substring(cursorPos)
                      const triggerMatch = textBefore.match(/([@#])(\S*)$/)
                      if (triggerMatch) {
                        const start = cursorPos - triggerMatch[0].length
                        setValue(value.substring(0, start) + `@${selected.username} ` + textAfter)
                      }
                    }
                  } else if (server) {
                    const matches = server.channels.filter((c) => c.type === 'text' && c.name.toLowerCase().includes(q)).slice(0, 5)
                    const selected = matches[autocomplete.selectedIndex]
                    if (selected) {
                      const cursorPos = inputRef.current?.selectionStart || value.length
                      const textBefore = value.substring(0, cursorPos)
                      const textAfter = value.substring(cursorPos)
                      const triggerMatch = textBefore.match(/([@#])(\S*)$/)
                      if (triggerMatch) {
                        const start = cursorPos - triggerMatch[0].length
                        setValue(value.substring(0, start) + `#${selected.name} ` + textAfter)
                      }
                    }
                  }
                  setAutocomplete({ isOpen: false, type: '@', query: '', selectedIndex: 0 })
                  return
                }
                if (e.key === 'Escape') {
                  e.preventDefault()
                  setAutocomplete({ isOpen: false, type: '@', query: '', selectedIndex: 0 })
                  return
                }
              }
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                if (value.trim() && slowmodeRemaining <= 0 && !isUploading) {
                  if (pendingAttachments.length > 0) {
                    // Simulate upload progress
                    setPendingAttachments((prev) => prev.map(a => ({ ...a, progress: 0 })))
                    const uploadInterval = setInterval(() => {
                      setPendingAttachments((prev) => {
                        const updated = prev.map(a => {
                          if (a.progress >= 100) return a
                          const increment = Math.floor(Math.random() * 16) + 15
                          return { ...a, progress: Math.min(100, a.progress + increment) }
                        })
                        if (updated.every(a => a.progress >= 100)) {
                          clearInterval(uploadInterval)
                          setTimeout(() => {
                            setPendingAttachments([])
                            addToast('success', 'Files uploaded successfully')
                          }, 300)
                        }
                        return updated
                      })
                    }, 200)
                  } else {
                    setPendingAttachments([])
                    addToast('info', 'Message sent')
                  }
                  setValue('')
                  setReplyingToMessageId(null)
                  if (channel?.slowmode) {
                    setSlowmodeRemaining(channel.slowmode)
                  }
                }
              }
              if (e.key === 'Escape' && replyingToMessageId) {
                setReplyingToMessageId(null)
              }
            }}
          />
          {/* Slowmode indicator */}
          {slowmodeRemaining > 0 && (
            <Text fontFamily="$fontMono" fontSize={11} color="$warning" paddingTop={4}>
              Slowmode — {slowmodeRemaining}s remaining
            </Text>
          )}
        </View>

        <InputButton onPress={() => setShowToolbar((v) => !v)}>
          <Type size={16} color={showToolbar ? '$accent' : '$sidebarMuted'} />
        </InputButton>

        <InputButton onPress={() => setEmojiPickerOpen(true)}>
          <Smile size={16} color="$sidebarMuted" />
        </InputButton>
      </InputContainer>

      {/* Markdown toolbar */}
      {showToolbar && (
        <ToolbarRow>
          <ToolbarButton
            onPress={() => inputRef.current && wrapSelection(inputRef.current, '**', '**', setValue)}
          >
            <Bold size={14} color="$sidebarMuted" />
          </ToolbarButton>
          <ToolbarButton
            onPress={() => inputRef.current && wrapSelection(inputRef.current, '*', '*', setValue)}
          >
            <Italic size={14} color="$sidebarMuted" />
          </ToolbarButton>
          <ToolbarButton
            onPress={() => inputRef.current && wrapSelection(inputRef.current, '`', '`', setValue)}
          >
            <Code size={14} color="$sidebarMuted" />
          </ToolbarButton>
          <ToolbarButton
            onPress={() => inputRef.current && wrapSelection(inputRef.current, '||', '||', setValue)}
          >
            <Text fontFamily="$fontMono" fontSize={12} fontWeight="700" color="$sidebarMuted">||</Text>
          </ToolbarButton>
        </ToolbarRow>
      )}
    </InputWrapper>
  )
}
